import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { syncLanguageWithUser } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Save, User, Bell, Mail, AtSign, Check, Loader2, Share2, Copy, Link as LinkIcon, AlertTriangle, Edit3, Camera, Trash2, Globe, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

const ProfilePage = () => {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    picture: "",
    cover_image: "",
    email_notifications: true,
    spoken_languages: ["en"],
    primary_language: "en",
    allow_proactive_insights: {
      swot: true, core_values: true, wheel_of_life: true, gratitude: true,
    },
    // Top-3 Core Values display visibility on the public profile page.
    // Default = "public" so the values are visible to anyone on first
    // sign-up (the user can lock them down later from this same screen).
    core_values_visibility: "public",
    // SWOT + Wheel of Life — default to "private" since these are more
    // personal than core values. Users opt in to share when they're ready.
    swot_visibility: "private",
    wheel_of_life_visibility: "private",
  });
  // Languages supported by the backend translator. Loaded once from
  // /api/i18n/languages so adding a new language is a server-only change.
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  // Username (@handle) state
  const [usernameDraft, setUsernameDraft] = useState("");
  const [usernameCheck, setUsernameCheck] = useState({ valid: true, available: true, reason: null });
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const usernameDebounceRef = useRef(null);
  // Type-to-confirm guard when user is changing an already-set username
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [confirmTyped, setConfirmTyped] = useState("");

  // Share-my-profile state
  const [copied, setCopied] = useState(false);

  // Profile picture upload
  const fileInputRef = useRef(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  // Cover image upload (1200×630 landscape banner)
  const coverInputRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Downscale + recompress an image file to a square 512×512 JPEG data URL.
  // Keeps the payload small enough to store on the user doc without
  // hammering Mongo. Center-crops to square so avatars always look clean.
  const downscaleToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const size = 512;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          // Center-crop: take the largest square from the center
          const shortSide = Math.min(img.width, img.height);
          const sx = (img.width - shortSide) / 2;
          const sy = (img.height - shortSide) / 2;
          ctx.drawImage(img, sx, sy, shortSide, shortSide, 0, 0, size, size);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handlePictureFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    // 8MB raw cap pre-compression — anything bigger is almost certainly
    // a mistake or a scanner; forces the user to pick a reasonable photo.
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large. Max 8 MB.");
      return;
    }
    setUploadingPicture(true);
    try {
      const dataUrl = await downscaleToDataUrl(file);
      setFormData((prev) => ({ ...prev, picture: dataUrl }));
      toast.success("Photo ready — click Save to apply");
    } catch {
      toast.error("Couldn't read that image. Try a different file.");
    } finally {
      setUploadingPicture(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearPicture = () => {
    setFormData((prev) => ({ ...prev, picture: "" }));
  };

  // Downscale a landscape image to a 1200×630 JPEG data URL for use as a
  // profile cover. Matches the OG share card dimensions — same aspect ratio
  // renders cleanly everywhere (web banner + link previews).
  const downscaleCoverToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const targetW = 1200;
          const targetH = 630;
          const canvas = document.createElement("canvas");
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext("2d");
          // Cover-fit: scale so the image fills, center-crop the overflow.
          const srcRatio = img.width / img.height;
          const dstRatio = targetW / targetH;
          let sx = 0, sy = 0, sw = img.width, sh = img.height;
          if (srcRatio > dstRatio) {
            // source is wider — crop sides
            sw = img.height * dstRatio;
            sx = (img.width - sw) / 2;
          } else {
            // source is taller — crop top/bottom
            sh = img.width / dstRatio;
            sy = (img.height - sh) / 2;
          }
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
          resolve(canvas.toDataURL("image/jpeg", 0.82));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleCoverFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Cover image too large. Max 10 MB.");
      return;
    }
    setUploadingCover(true);
    try {
      const dataUrl = await downscaleCoverToDataUrl(file);
      setFormData((prev) => ({ ...prev, cover_image: dataUrl }));
      toast.success("Cover ready — click Save to apply");
    } catch {
      toast.error("Couldn't read that image. Try a different file.");
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const clearCover = () => {
    // Empty string tells the backend to reset to the default mountain cover.
    setFormData((prev) => ({ ...prev, cover_image: "" }));
  };

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API}/profile`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          name: data.name || "",
          bio: data.bio || "",
          picture: data.picture || "",
          cover_image: data.cover_image || "",
          email_notifications: data.email_notifications !== false,
          spoken_languages: Array.isArray(data.spoken_languages) && data.spoken_languages.length
            ? data.spoken_languages
            : ["en"],
          primary_language: data.primary_language || "en",
          // Per-tool dict. Backend may return legacy bool — coerce.
          allow_proactive_insights: (() => {
            const v = data.allow_proactive_insights;
            const fallback = { swot: true, core_values: true, wheel_of_life: true, gratitude: true };
            if (typeof v === "boolean") return { swot: v, core_values: v, wheel_of_life: v, gratitude: v };
            if (v && typeof v === "object") return { ...fallback, ...v };
            return fallback;
          })(),
          core_values_visibility: ["public", "circle", "private"].includes(data.core_values_visibility)
            ? data.core_values_visibility
            : "public",
          swot_visibility: ["public", "circle", "private"].includes(data.swot_visibility)
            ? data.swot_visibility
            : "private",
          wheel_of_life_visibility: ["public", "circle", "private"].includes(data.wheel_of_life_visibility)
            ? data.wheel_of_life_visibility
            : "private",
        });
        setUsernameDraft(data.username || "");
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced live availability check for the @username field
  useEffect(() => {
    if (!profile) return;
    const trimmed = (usernameDraft || "").trim().toLowerCase();
    if (!trimmed || trimmed === profile.username) {
      setUsernameCheck({ valid: true, available: true, reason: null });
      setUsernameChecking(false);
      return;
    }
    setUsernameChecking(true);
    if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    usernameDebounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/profile/username/check?value=${encodeURIComponent(trimmed)}`, {
          credentials: 'include',
        });
        if (r.ok) setUsernameCheck(await r.json());
      } catch {
        // silent — server-side validation will still catch it on save
      } finally {
        setUsernameChecking(false);
      }
    }, 350);
    return () => {
      if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    };
  }, [usernameDraft, profile]);

  const saveUsername = async () => {
    const trimmed = (usernameDraft || "").trim().toLowerCase();
    if (!trimmed) return;
    setSavingUsername(true);
    try {
      const r = await fetch(`${API}/profile/username`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: trimmed }),
      });
      const data = await r.json();
      if (r.ok) {
        setProfile((p) => ({ ...p, username: data.username, username_set_explicitly: true }));
        setUsernameDraft(data.username);
        setChangeDialogOpen(false);
        setConfirmTyped("");
        toast.success(`Saved @${data.username}`);
      } else {
        toast.error(data.detail || 'Could not save username');
      }
    } catch {
      toast.error('Could not save username');
    } finally {
      setSavingUsername(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Load the list of supported languages once — the picker renders directly
  // from this so adding a new language on the backend requires no frontend change.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/i18n/languages`, { credentials: "include" });
        if (r.ok) {
          const data = await r.json();
          setSupportedLanguages(data.languages || []);
        }
      } catch {
        /* non-fatal — the UI falls back to showing whatever the user already has */
      }
    })();
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        setProfile(updatedProfile);
        setUser(prev => ({
          ...prev,
          name: updatedProfile.name,
          picture: updatedProfile.picture,
          primary_language: updatedProfile.primary_language,
          spoken_languages: updatedProfile.spoken_languages,
        }));
        // Swap the UI language immediately if the primary changed.
        syncLanguageWithUser(updatedProfile);
        toast.success('Profile updated successfully');
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="profile-page">
      {/* Page Title */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              {t("profile.title")}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{t("profile.manageAccount")}</p>
          </div>
          <Button 
            onClick={saveProfile}
            disabled={saving}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
            data-testid="save-profile-button"
          >
            {saving ? t("common.saving") : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t("profile.saveChanges")}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Profile Picture Section */}
        <div className="card p-6 mb-6" data-testid="profile-picture-section">
          <div className="flex items-start gap-5 flex-wrap sm:flex-nowrap">
            <div className="relative flex-shrink-0">
              <Avatar className="h-24 w-24" style={{ border: "2px solid var(--color-border-default)" }}>
                <AvatarImage src={formData.picture} alt={formData.name} />
                <AvatarFallback className="bg-violet-100 text-violet-600 text-3xl">
                  {formData.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              {/* Camera overlay button — floats on the bottom-right of the avatar */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
                className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                style={{
                  background: "var(--color-brand-primary)",
                  color: "var(--color-text-on-brand)",
                  border: "2px solid var(--color-surface)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                }}
                aria-label="Change profile picture"
                data-testid="picture-upload-btn"
                title="Upload new photo"
              >
                {uploadingPicture ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'system-ui, sans-serif' }}>
                {t("profile.profilePicture")}
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                Upload a photo from your device. We'll crop it to a 512×512 square — it shows up on your profile, wall, and when friends see your gratitude.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPicture}
                  className="h-9 text-xs"
                  data-testid="picture-upload-secondary"
                >
                  {uploadingPicture ? (
                    <><Loader2 size={12} className="mr-1.5 animate-spin" /> {t("common.processing")}</>
                  ) : (
                    <><Camera size={12} className="mr-1.5" /> {t("profile.uploadPicture")}</>
                  )}
                </Button>
                {formData.picture && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearPicture}
                    className="h-9 text-xs text-slate-500"
                    data-testid="picture-remove-btn"
                  >
                    <Trash2 size={12} className="mr-1.5" /> {t("common.remove")}
                  </Button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handlePictureFile(e.target.files?.[0])}
                className="hidden"
                data-testid="picture-file-input"
              />
            </div>
          </div>
        </div>

        {/* Cover Image Section */}
        <div className="card p-6 mb-6" data-testid="cover-image-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'system-ui, sans-serif' }}>
                {t("profile.coverImage")}
              </h3>
              <p className="text-xs text-slate-500">
                {t("profile.coverImageHint")}
              </p>
            </div>
          </div>

          {/* Preview strip */}
          <div
            className="relative rounded-xl overflow-hidden mb-3 border"
            style={{
              borderColor: "var(--color-border-default)",
              aspectRatio: "1200 / 400",
              background: "linear-gradient(135deg, #1f1a3a 0%, #5d4481 50%, #e8b78a 100%)",
            }}
            data-testid="cover-preview"
          >
            <img
              src={formData.cover_image || "/api/assets/default-cover.jpg"}
              alt="Profile cover preview"
              className="w-full h-full object-cover"
            />
            {!formData.cover_image && (
              <span className="absolute bottom-2 left-3 text-[10px] font-medium px-2 py-0.5 rounded-full shadow-sm" style={{ background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(8px)" }}>
                Default cover
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              onClick={() => coverInputRef.current?.click()}
              disabled={uploadingCover}
              className="h-9 text-xs"
              data-testid="cover-upload-btn"
            >
              {uploadingCover ? (
                <><Loader2 size={12} className="mr-1.5 animate-spin" /> Processing…</>
              ) : (
                <><Camera size={12} className="mr-1.5" /> Upload cover</>
              )}
            </Button>
            {formData.cover_image && (
              <Button
                type="button"
                variant="outline"
                onClick={clearCover}
                className="h-9 text-xs text-slate-500"
                data-testid="cover-reset-btn"
              >
                <Trash2 size={12} className="mr-1.5" /> Reset to default
              </Button>
            )}
          </div>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => handleCoverFile(e.target.files?.[0])}
            className="hidden"
            data-testid="cover-file-input"
          />
        </div>

        {/* Languages */}
        <div className="card p-6 mb-6" data-testid="languages-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'system-ui, sans-serif' }}>
                {t("profile.languages")}
              </h3>
              <p className="text-xs text-slate-500">
                {t("profile.languagesHint")}
              </p>
            </div>
          </div>

          {supportedLanguages.length === 0 ? (
            <p className="text-xs text-slate-400">Loading languages…</p>
          ) : (
            <>
              {/* Fluent-languages multi-select chips */}
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">{t("profile.fluentIn")}</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {supportedLanguages.map(lang => {
                    const active = formData.spoken_languages.includes(lang.code);
                    // The primary language is always implicitly spoken — prevent
                    // the user from un-ticking it (avoids an impossible state).
                    const isPrimary = formData.primary_language === lang.code;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => {
                          if (isPrimary) return;
                          setFormData(prev => ({
                            ...prev,
                            spoken_languages: prev.spoken_languages.includes(lang.code)
                              ? prev.spoken_languages.filter(c => c !== lang.code)
                              : [...prev.spoken_languages, lang.code],
                          }));
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all"
                        style={{
                          background: active ? "var(--color-brand-primary)" : "transparent",
                          color: active ? "#fff" : "var(--color-text-primary)",
                          border: `1px solid ${active ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                          cursor: isPrimary ? "default" : "pointer",
                          opacity: isPrimary ? 0.95 : 1,
                        }}
                        data-testid={`lang-fluent-${lang.code}`}
                        title={isPrimary ? "Primary language is always fluent" : (active ? "Click to remove" : "Click to add")}
                      >
                        <span>{lang.flag}</span>
                        {lang.native}
                        {isPrimary && <span className="text-[10px] opacity-70">· {t("profile.primaryBadge")}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Primary language picker */}
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-wide text-slate-500">
                  {t("profile.primaryLanguage")}
                  <span className="ml-1 normal-case text-slate-400">({t("profile.primaryLanguageHint")})</span>
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {supportedLanguages.map(lang => {
                    const active = formData.primary_language === lang.code;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            primary_language: lang.code,
                            // Make sure the primary is also in the fluent list.
                            spoken_languages: prev.spoken_languages.includes(lang.code)
                              ? prev.spoken_languages
                              : [lang.code, ...prev.spoken_languages],
                          }));
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all"
                        style={{
                          background: active ? "var(--color-brand-primary)" : "transparent",
                          color: active ? "#fff" : "var(--color-text-primary)",
                          border: `1px solid ${active ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                        }}
                        data-testid={`lang-primary-${lang.code}`}
                      >
                        <span>{lang.flag}</span>
                        {lang.native}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Plan summary */}
        <div className="card p-5 mb-6" data-testid="plan-summary-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                {t("profile.planLabel")}
              </div>
              <div className="text-base font-semibold text-slate-900 capitalize mt-0.5">
                {user?.plan === 'unlimited'
                  ? t('profile.planUnlimited')
                  : t('profile.planBasic', { limit: 4 })}
              </div>
              {user?.plan !== 'unlimited' && (
                <div className="text-xs text-slate-500 mt-1">
                  {t('profile.planBasicHint')}
                </div>
              )}
            </div>
            {user?.plan === 'unlimited' ? (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700"
                data-testid="plan-badge"
              >
                {t('profile.planUnlimitedBadge')}
              </span>
            ) : (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600"
                data-testid="plan-badge"
              >
                {t('profile.planBasicBadge')}
              </span>
            )}
          </div>
        </div>

        {/* Personal Information */}
        <div className="card p-6 mb-6" data-testid="personal-info-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-violet-600" />
            </div>
            <h3 
              className="font-semibold text-slate-900"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              {t("profile.personalInfo")}
            </h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                {t("profile.displayName")}
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1"
                data-testid="name-input"
              />
            </div>

            <div>
              <Label htmlFor="bio" className="text-sm font-medium text-slate-700">
                Tagline
                <span className="ml-1 text-xs font-normal text-slate-400">
                  (one line on your public profile)
                </span>
              </Label>
              <Input
                id="bio"
                value={formData.bio}
                onChange={(e) => {
                  const v = e.target.value.slice(0, 160);
                  setFormData({ ...formData, bio: v });
                }}
                maxLength={160}
                placeholder="Leading calmer teams and learning to slow down on purpose."
                className="mt-1"
                data-testid="bio-input"
              />
              <p className="text-[10px] text-slate-400 text-right mt-0.5">
                {(formData.bio || "").length}/160
              </p>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700">
                {t("profile.email")}
              </Label>
              <Input
                value={profile?.email || ""}
                disabled
                className="mt-1 bg-slate-50"
              />
              <p className="text-xs text-slate-500 mt-1">
                Email cannot be changed (linked to Google account)
              </p>
            </div>
          </div>
        </div>

        {/* Username (@handle) */}
        <div className="card p-6 mb-6" data-testid="username-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <AtSign className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'system-ui, sans-serif' }}>
                Username
              </h3>
              <p className="text-xs text-slate-500">
                Used in search, public profiles, and discussions. 3–20 chars, letters, numbers, _ or .
              </p>
            </div>
          </div>

          {/* If user has never confirmed, show the inline compose flow (low friction for first pick).
              If already set explicitly, show a read-only view + "Change username" link that opens a
              confirmation dialog with a warning + type-to-confirm guard. */}
          {profile?.username_set_explicitly ? (
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
              data-testid="username-readonly-row"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-slate-400 text-sm font-medium flex-shrink-0">@</span>
                <span className="text-sm font-semibold text-slate-800 truncate"
                  data-testid="username-readonly-value"
                >
                  {profile.username}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setUsernameDraft("");
                  setConfirmTyped("");
                  setUsernameCheck({ valid: true, available: true, reason: null });
                  setChangeDialogOpen(true);
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-800 hover:underline"
                data-testid="username-change-link"
              >
                <Edit3 className="w-3.5 h-3.5" /> Change username
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium pointer-events-none">@</span>
                  <Input
                    value={usernameDraft}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\s+/g, '').toLowerCase();
                      setUsernameDraft(v);
                    }}
                    placeholder="your.username"
                    maxLength={20}
                    className="pl-7"
                    data-testid="username-input"
                  />
                  {usernameChecking && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                  )}
                  {!usernameChecking && usernameDraft && usernameDraft !== profile?.username && usernameCheck.valid && usernameCheck.available && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                  )}
                </div>
                <Button
                  onClick={saveUsername}
                  disabled={
                    savingUsername ||
                    usernameChecking ||
                    !usernameDraft ||
                    !usernameCheck.valid ||
                    !usernameCheck.available
                  }
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                  data-testid="username-save-button"
                >
                  {savingUsername ? 'Saving…' : 'Confirm'}
                </Button>
              </div>

              {/* Inline status line (first-time pick flow) */}
              <div className="mt-2 min-h-[1.25rem] text-xs" data-testid="username-status">
                {usernameDraft && usernameDraft !== profile?.username && !usernameCheck.valid && (
                  <span className="text-red-600">{usernameCheck.reason}</span>
                )}
                {usernameDraft && usernameDraft !== profile?.username && usernameCheck.valid && !usernameCheck.available && (
                  <span className="text-red-600">{usernameCheck.reason || 'That username is already taken'}</span>
                )}
                {usernameDraft && usernameDraft !== profile?.username && usernameCheck.valid && usernameCheck.available && !usernameChecking && (
                  <span className="text-emerald-600 font-medium">@{usernameDraft} is available</span>
                )}
                {profile?.username && usernameDraft === profile.username && (
                  <span className="text-slate-500">
                    Current: <span className="font-medium text-slate-700">@{profile.username}</span>
                    <span className="ml-1 text-violet-600 font-medium">(auto-generated — tap Confirm to save)</span>
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Change-username dialog — type-to-confirm guard against accidental edits */}
        <Dialog open={changeDialogOpen} onOpenChange={(v) => {
          setChangeDialogOpen(v);
          if (!v) { setUsernameDraft(""); setConfirmTyped(""); }
        }}>
          <DialogContent className="sm:max-w-md" data-testid="username-change-dialog">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              <h2 className="text-base font-semibold text-slate-900">Change your username</h2>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4">
              <p className="text-xs text-amber-900 leading-relaxed">
                <strong>This will break every link you&apos;ve shared.</strong> Anyone who has
                <span className="font-mono"> /{profile?.username}</span> bookmarked or linked in their
                bio will see a &ldquo;profile not found&rdquo; page. Pick carefully.
              </p>
            </div>

            <div className="space-y-3.5">
              <div>
                <Label className="text-xs font-medium text-slate-600">New username</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium pointer-events-none">@</span>
                  <Input
                    value={usernameDraft}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\s+/g, '').toLowerCase();
                      setUsernameDraft(v);
                    }}
                    placeholder="your.new.handle"
                    maxLength={20}
                    className="pl-7"
                    autoFocus
                    data-testid="username-change-new-input"
                  />
                  {usernameChecking && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                  )}
                  {!usernameChecking && usernameDraft && usernameDraft !== profile?.username && usernameCheck.valid && usernameCheck.available && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                  )}
                </div>
                <div className="mt-1.5 min-h-[1.1rem] text-xs">
                  {usernameDraft && !usernameCheck.valid && (
                    <span className="text-red-600">{usernameCheck.reason}</span>
                  )}
                  {usernameDraft && usernameCheck.valid && !usernameCheck.available && (
                    <span className="text-red-600">{usernameCheck.reason || 'That username is already taken'}</span>
                  )}
                  {usernameDraft && usernameCheck.valid && usernameCheck.available && !usernameChecking && usernameDraft !== profile?.username && (
                    <span className="text-emerald-600 font-medium">@{usernameDraft} is available</span>
                  )}
                </div>
              </div>

              {/* Type-to-confirm: user must retype the new handle exactly */}
              {usernameDraft && usernameCheck.valid && usernameCheck.available && usernameDraft !== profile?.username && (
                <div>
                  <Label className="text-xs font-medium text-slate-600">
                    Type <span className="font-mono text-slate-800">@{usernameDraft}</span> to confirm
                  </Label>
                  <Input
                    value={confirmTyped}
                    onChange={(e) => setConfirmTyped(e.target.value.replace(/^@/, '').toLowerCase())}
                    placeholder={`@${usernameDraft}`}
                    maxLength={21}
                    className="mt-1 font-mono text-sm"
                    data-testid="username-change-confirm-input"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
              <Button variant="ghost"
                onClick={() => { setChangeDialogOpen(false); setUsernameDraft(""); setConfirmTyped(""); }}
                data-testid="username-change-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={saveUsername}
                disabled={
                  savingUsername ||
                  usernameChecking ||
                  !usernameDraft ||
                  usernameDraft === profile?.username ||
                  !usernameCheck.valid ||
                  !usernameCheck.available ||
                  confirmTyped !== usernameDraft
                }
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                data-testid="username-change-confirm"
              >
                {savingUsername ? 'Saving…' : 'Change username'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Share my public profile */}
        {profile?.username && (() => {
          const handle = profile.username;
          const origin = typeof window !== 'undefined' ? window.location.origin : '';
          // Canonical human URL — shown as "display URL" text, used for
          // browser-bar navigation. WhatsApp/Twitter/Slack see the shim URL
          // which redirects humans here after a split-second.
          const humanUrl = `${origin}/${handle}`;
          // Shim URL — what the Copy Link / Share buttons copy. Backend
          // serves personalized og:title + og:image + og:description, then
          // emits a meta-refresh so real browsers land on `humanUrl`.
          const url = `${origin}/api/share/${handle}`;
          // Display URL shown next to the copy-link input — we keep it
          // brand-friendly by showing the human URL, not the shim path.
          const displayUrl = humanUrl.replace(/^https?:\/\//, '');

          const copy = async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied(true);
              toast.success('Link copied');
              setTimeout(() => setCopied(false), 1800);
            } catch {
              toast.error("Couldn't copy — try selecting the link");
            }
          };

          const nativeShare = async () => {
            const data = {
              title: `${profile.name || handle} on Evoluras`,
              text: `My profile on Evoluras — @${handle}`,
              url,
            };
            if (navigator.share) {
              try { await navigator.share(data); } catch { /* user cancelled */ }
            } else {
              await copy();
            }
          };

          const shareText = encodeURIComponent(`My Evoluras profile — @${handle}`);
          const shareUrl = encodeURIComponent(url);

          return (
            <div className="card p-6 mb-6" data-testid="share-profile-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-fuchsia-100 rounded-lg flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-fuchsia-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Share your profile</h3>
                  <p className="text-xs text-slate-500">
                    Drop this in your bio, email signature, or anywhere people should find you.
                  </p>
                </div>
              </div>

              {/* URL row with inline copy morph */}
              <div
                className="flex items-stretch rounded-xl overflow-hidden border transition-colors"
                style={{ borderColor: copied ? '#10b981' : '#e2e8f0' }}
                data-testid="share-profile-url-row"
              >
                <div className="flex items-center gap-2 px-3 flex-1 min-w-0 bg-slate-50">
                  <LinkIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-700 truncate font-mono"
                    data-testid="share-profile-url"
                  >
                    {displayUrl}
                  </span>
                </div>
                <button
                  onClick={copy}
                  className="px-4 text-xs font-semibold text-white transition-all"
                  style={{
                    background: copied ? '#10b981' : '#8b5cf6',
                    minWidth: 92,
                  }}
                  data-testid="share-profile-copy"
                >
                  {copied ? (
                    <span className="flex items-center justify-center gap-1">
                      <Check className="w-3.5 h-3.5" /> Copied
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-1">
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </span>
                  )}
                </button>
              </div>

              {/* Share targets */}
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button
                  onClick={nativeShare}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white rounded-full transition-colors"
                  data-testid="share-profile-native"
                >
                  <Share2 className="w-3.5 h-3.5" /> Share
                </button>

                <a
                  href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-full transition-colors"
                  data-testid="share-profile-x"
                >
                  <span className="font-bold">X</span>
                </a>

                <a
                  href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-full transition-colors"
                  data-testid="share-profile-whatsapp"
                >
                  WhatsApp
                </a>

                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-full transition-colors"
                  data-testid="share-profile-linkedin"
                >
                  LinkedIn
                </a>

                <a
                  href={`mailto:?subject=${encodeURIComponent('My Evoluras profile')}&body=${shareText}%20${shareUrl}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full transition-colors"
                  data-testid="share-profile-email"
                >
                  Email
                </a>
              </div>
            </div>
          );
        })()}

        {/* Notification Preferences */}
        <div className="card p-6" data-testid="notification-settings-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-purple-600" />
            </div>
            <h3 
              className="font-semibold text-slate-900"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              Notification Preferences
            </h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Email Notifications</p>
                  <p className="text-sm text-slate-500">
                    Receive email when someone requests your insights
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.email_notifications}
                onCheckedChange={(checked) => setFormData({ ...formData, email_notifications: checked })}
                data-testid="email-notifications-switch"
              />
            </div>
            <div className="py-3">
              <div className="flex items-start gap-3 mb-3">
                <Sparkles className="w-5 h-5 text-violet-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{t("profile.coreValuesVisibilityTitle", { defaultValue: "Top values on your profile" })}</p>
                  <p className="text-sm text-slate-500">
                    {t("profile.coreValuesVisibilityHint", { defaultValue: "Choose who can see your top 3 core values when they visit your profile page." })}
                  </p>
                </div>
              </div>
              <VisibilitySelector
                value={formData.core_values_visibility}
                onChange={(v) => setFormData(prev => ({ ...prev, core_values_visibility: v }))}
                testIdPrefix="core-values-vis"
                t={t}
              />
            </div>
            <div className="py-3">
              <div className="flex items-start gap-3 mb-3">
                <Sparkles className="w-5 h-5 text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{t("profile.swotVisibilityTitle", { defaultValue: "Top strengths on your profile" })}</p>
                  <p className="text-sm text-slate-500">
                    {t("profile.swotVisibilityHint", { defaultValue: "Choose who can see your top 3 SWOT strengths. Weaknesses, opportunities, and threats are never shown publicly." })}
                  </p>
                </div>
              </div>
              <VisibilitySelector
                value={formData.swot_visibility}
                onChange={(v) => setFormData(prev => ({ ...prev, swot_visibility: v }))}
                testIdPrefix="swot-vis"
                t={t}
              />
            </div>
            <div className="py-3">
              <div className="flex items-start gap-3 mb-3">
                <Sparkles className="w-5 h-5 text-violet-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{t("profile.wheelVisibilityTitle", { defaultValue: "Wheel of Life on your profile" })}</p>
                  <p className="text-sm text-slate-500">
                    {t("profile.wheelVisibilityHint", { defaultValue: "Choose who can see your 8-area Wheel of Life summary on your profile page." })}
                  </p>
                </div>
              </div>
              <VisibilitySelector
                value={formData.wheel_of_life_visibility}
                onChange={(v) => setFormData(prev => ({ ...prev, wheel_of_life_visibility: v }))}
                testIdPrefix="wheel-vis"
                t={t}
              />
            </div>
            <div className="py-3">
              <div className="flex items-start gap-3 mb-3">
                <Sparkles className="w-5 h-5 text-violet-400 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">{t("profile.allowProactiveInsightsTitle")}</p>
                  <p className="text-sm text-slate-500">
                    {t("profile.allowProactiveInsightsHint")}
                  </p>
                </div>
              </div>
              <div className="ml-8 space-y-2.5">
                {[
                  { key: "swot", label: t("profile.toolSwot") },
                  { key: "core_values", label: t("profile.toolCoreValues") },
                  { key: "wheel_of_life", label: t("profile.toolWheelOfLife") },
                  { key: "gratitude", label: t("profile.toolGratitude") },
                ].map(tool => (
                  <div key={tool.key} className="flex items-center justify-between" data-testid={`proactive-row-${tool.key}`}>
                    <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>{tool.label}</span>
                    <Switch
                      checked={!!formData.allow_proactive_insights?.[tool.key]}
                      onCheckedChange={(checked) =>
                        setFormData(prev => ({
                          ...prev,
                          allow_proactive_insights: {
                            ...(prev.allow_proactive_insights || {}),
                            [tool.key]: checked,
                          },
                        }))
                      }
                      data-testid={`allow-proactive-${tool.key}-switch`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>Account created: {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</p>
          <p className="mt-2">
            <Link
              to="/privacy"
              className="hover:underline transition-colors"
              style={{ color: "var(--color-text-interactive)" }}
              data-testid="profile-privacy-link"
            >
              {t("common.privacyPolicy")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// ── VisibilitySelector ──────────────────────────────────────────────
// 3-tile segmented control used by the Settings panel for Core Values,
// SWOT, and Wheel of Life visibility. Brand-purple active state, dark-glass
// inactive state. Test ids follow `${testIdPrefix}-${public|circle|private}`.
const VisibilitySelector = ({ value, onChange, testIdPrefix, t }) => {
  const opts = [
    { key: "public",  label: t("profile.visPublic",  { defaultValue: "Public" }),  hint: t("profile.visPublicHint",  { defaultValue: "Anyone" }) },
    { key: "circle",  label: t("profile.visCircle",  { defaultValue: "Circle" }),  hint: t("profile.visCircleHint",  { defaultValue: "Inner Circle only" }) },
    { key: "private", label: t("profile.visPrivate", { defaultValue: "Private" }), hint: t("profile.visPrivateHint", { defaultValue: "Only you" }) },
  ];
  return (
    <div className="ml-8 grid grid-cols-3 gap-2" data-testid={`${testIdPrefix}-group`}>
      {opts.map(opt => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className={`rounded-lg p-2.5 text-left transition-all ${active ? "ring-2" : "hover:bg-white/5"}`}
            style={{
              background: active ? "rgba(124,58,237,0.12)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${active ? "rgba(124,58,237,0.45)" : "rgba(255,255,255,0.10)"}`,
              ["--tw-ring-color"]: "rgba(124,58,237,0.45)",
            }}
            data-testid={`${testIdPrefix}-${opt.key}`}
          >
            <div className="text-sm font-semibold" style={{ color: active ? "var(--color-brand-primary, #7c3aed)" : "var(--color-text-primary)" }}>
              {opt.label}
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
              {opt.hint}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ProfilePage;
